import React from 'react';
import { Lock, AlertTriangle, Database, CheckCircle2, ShieldCheck, KeyRound, MessageSquare } from 'lucide-react';
import { DOCS_CODE } from '../docs-code-snippets';

export const SecurityPage: React.FC = () => {
  return (
    <div className="canvas-dark" style={{ minHeight: '100vh', paddingTop: '130px', paddingBottom: '96px', backgroundColor: '#121212' }}>
      
      {/* Header */}
      <header className="container" style={{ padding: '0 20px 48px', maxWidth: '960px' }}>
        <div className="eyebrow" style={{ color: '#6D7CFF', marginBottom: '12px' }}>
          Enterprise Trust &amp; Cryptography
        </div>
        <h1 
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: 'clamp(32px, 5vw, 52px)',
            fontWeight: 400,
            lineHeight: 1.15,
            letterSpacing: '-1.2px',
            color: '#FFFFFF',
            margin: '0 0 16px'
          }}
        >
          Security &amp; Cryptographic Architecture
        </h1>
        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: '16px', color: '#D7D7D7', lineHeight: 1.6, maxWidth: '780px', margin: 0 }}>
          Draway Auth is architected under an adversarial zero-trust threat model. We assume host networks can be eavesdropped, public endpoints will face automated toll fraud, and database snapshots can be leaked.
        </p>
      </header>

      <main className="container" style={{ maxWidth: '960px', padding: '0 20px', display: 'flex', flexDirection: 'column', gap: '56px' }}>
        
        {/* Core Threat Model Pillars */}
        <section>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            
            {/* Pillar 1: Argon2id */}
            <div 
              style={{
                backgroundColor: '#1A1A1A',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                padding: '28px 24px',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: 'rgba(109, 124, 255, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6D7CFF' }}>
                  <Lock size={20} />
                </div>
                <span style={{ fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", color: '#6D7CFF', backgroundColor: 'rgba(109, 124, 255, 0.12)', padding: '3px 8px', borderRadius: '9999px', letterSpacing: '0.04em' }}>
                  PHC WINNER
                </span>
              </div>
              <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '18px', fontWeight: 500, color: '#FFFFFF', margin: '0 0 10px' }}>
                Memory-Hard Argon2id
              </h3>
              <p style={{ fontSize: '14px', color: '#D7D7D7', lineHeight: 1.6, margin: 0, flexGrow: 1 }}>
                Legacy bcrypt and SHA256 can be cracked at millions of attempts per second on consumer GPU arrays. Draway Auth uses Argon2id with memory-cost parameters designed to render hardware-accelerated dictionary attacks mathematically infeasible.
              </p>
            </div>

            {/* Pillar 2: AES-256-GCM */}
            <div 
              style={{
                backgroundColor: '#1A1A1A',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                padding: '28px 24px',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: 'rgba(109, 124, 255, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6D7CFF' }}>
                  <Database size={20} />
                </div>
                <span style={{ fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", color: '#6D7CFF', backgroundColor: 'rgba(109, 124, 255, 0.12)', padding: '3px 8px', borderRadius: '9999px', letterSpacing: '0.04em' }}>
                  AUTHENTICATED TAGS
                </span>
              </div>
              <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '18px', fontWeight: 500, color: '#FFFFFF', margin: '0 0 10px' }}>
                AES-256-GCM Vault
              </h3>
              <p style={{ fontSize: '14px', color: '#D7D7D7', lineHeight: 1.6, margin: 0, flexGrow: 1 }}>
                Sensitive multi-factor seeds and recovery keys are never stored in plaintext. They are encrypted at rest using AES-256 in Galois/Counter Mode with 128-bit authentication tags. Server refuses to boot without the mandatory 64-character <code>MFA_ENC_KEY</code>.
              </p>
            </div>

            {/* Pillar 3: Lockout Engine */}
            <div 
              style={{
                backgroundColor: '#1A1A1A',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                padding: '28px 24px',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: 'rgba(109, 124, 255, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6D7CFF' }}>
                  <AlertTriangle size={20} />
                </div>
                <span style={{ fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", color: '#6D7CFF', backgroundColor: 'rgba(109, 124, 255, 0.12)', padding: '3px 8px', borderRadius: '9999px', letterSpacing: '0.04em' }}>
                  15-MIN COOLDOWN
                </span>
              </div>
              <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '18px', fontWeight: 500, color: '#FFFFFF', margin: '0 0 10px' }}>
                Stateful Lockout Engine
              </h3>
              <p style={{ fontSize: '14px', color: '#D7D7D7', lineHeight: 1.6, margin: 0, flexGrow: 1 }}>
                Automated 15-minute freeze on 5 consecutive failed MFA attempts. Tracks failures in the database with strict <code>mfa_locked_until</code> timestamps, preventing automated credential stuffing and bot farm brute-forcing.
              </p>
            </div>

          </div>
        </section>

        {/* Cryptographic Storage Format Specification */}
        <section id="argon2">
          <div style={{ marginBottom: '16px' }}>
            <div className="eyebrow" style={{ color: '#6D7CFF', marginBottom: '6px' }}>Implementation Specification</div>
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '24px', fontWeight: 400, color: '#FFFFFF', margin: 0 }}>
              Cryptographic Storage Format
            </h2>
          </div>

          <div 
            style={{
              backgroundColor: '#1A1A1A',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '8px',
              padding: '20px',
              overflowX: 'auto'
            }}
          >
            <pre style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', lineHeight: 1.6, color: '#D7D7D7' }}>
              <code dangerouslySetInnerHTML={{ __html: DOCS_CODE.crypto }} />
            </pre>
          </div>
        </section>

        {/* Attack Surface Mitigation */}
        <section id="lockout">
          <div style={{ marginBottom: '16px' }}>
            <div className="eyebrow" style={{ color: '#6D7CFF', marginBottom: '6px' }}>Attack Surface Mitigation</div>
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '24px', fontWeight: 400, color: '#FFFFFF', margin: 0 }}>
              TOTP Anti-Replay &amp; Session Isolation
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
            <div 
              style={{
                backgroundColor: '#1A1A1A',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                padding: '24px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <ShieldCheck size={20} color="#6D7CFF" />
                <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '17px', fontWeight: 500, color: '#FFFFFF', margin: 0 }}>
                  RFC 6238 Anti-Replay Protection
                </h3>
              </div>
              <p style={{ fontSize: '14px', color: '#D7D7D7', lineHeight: 1.6, margin: 0 }}>
                TOTP codes are mathematically valid across 30-to-60-second windows. Draway tracks <code>last_used_totp</code> per account. If a code is replayed within the same time window, the second attempt is rejected immediately with an HTTP 400 error.
              </p>
            </div>

            <div 
              style={{
                backgroundColor: '#1A1A1A',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                padding: '24px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <KeyRound size={20} color="#6D7CFF" />
                <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '17px', fontWeight: 500, color: '#FFFFFF', margin: 0 }}>
                  Strict Session Boundary Isolation
                </h3>
              </div>
              <p style={{ fontSize: '14px', color: '#D7D7D7', lineHeight: 1.6, margin: 0 }}>
                During Phase 1, Draway Auth returns a signed, short-lived <code>mfaToken</code> with a 5-minute hard expiry. No access token or user session is created until Phase 2 is verified, preventing unauthorized resource enumeration.
              </p>
            </div>
          </div>
        </section>

        {/* Telephony & Toll-Fraud Defense */}
        <section id="toll-fraud-security">
          <div style={{ marginBottom: '16px' }}>
            <div className="eyebrow" style={{ color: '#6D7CFF', marginBottom: '6px' }}>Financial Attack Surface</div>
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '24px', fontWeight: 400, color: '#FFFFFF', margin: 0 }}>
              SMS Toll-Fraud &amp; Telephony Defense
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
            <div 
              style={{
                backgroundColor: '#1A1A1A',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                padding: '24px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <AlertTriangle size={20} color="#6D7CFF" />
                <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '17px', fontWeight: 500, color: '#FFFFFF', margin: 0 }}>
                  Ratio-Based Auto-Circuit Breaker
                </h3>
              </div>
              <p style={{ fontSize: '14px', color: '#D7D7D7', lineHeight: 1.6, margin: 0 }}>
                Attackers monetize premium telecom rates by pumping OTP requests. Draway tracks real-time verification ratios: if failed verifications exceed 20% within 5 minutes, the circuit trips automatically, triggering Cloudflare Turnstile proof-of-work challenges.
              </p>
            </div>

            <div 
              style={{
                backgroundColor: '#1A1A1A',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                padding: '24px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <MessageSquare size={20} color="#6D7CFF" />
                <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '17px', fontWeight: 500, color: '#FFFFFF', margin: 0 }}>
                  WhatsApp Business Channel Route
                </h3>
              </div>
              <p style={{ fontSize: '14px', color: '#D7D7D7', lineHeight: 1.6, margin: 0 }}>
                WhatsApp OTP completely neutralizes SMS telephony pumping because rogue carriers cannot extract premium termination surcharges from Meta. Draway provides seamless fallback with 98% open rates and fixed transparent pricing.
              </p>
            </div>
          </div>
        </section>

        {/* Cryptographic User Impersonation */}
        <section id="impersonation-security">
          <div style={{ marginBottom: '16px' }}>
            <div className="eyebrow" style={{ color: '#6D7CFF', marginBottom: '6px' }}>Enterprise Operational Trust</div>
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '24px', fontWeight: 400, color: '#FFFFFF', margin: 0 }}>
              Cryptographic User Impersonation (Support Mode)
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
            <div 
              style={{
                backgroundColor: '#1A1A1A',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                padding: '24px'
              }}
            >
              <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '17px', fontWeight: 500, color: '#FFFFFF', margin: '0 0 10px' }}>
                Time-Bounded HMAC-SHA256 Sessions
              </h3>
              <p style={{ fontSize: '14px', color: '#D7D7D7', lineHeight: 1.6, margin: 0 }}>
                Eliminate dangerous ad-hoc developer backdoors. Impersonation tokens are cryptographically signed with 15-minute maximum TTLs, locked to the customer support ticket ID, and restricted from changing user credentials or payment methods.
              </p>
            </div>

            <div 
              style={{
                backgroundColor: '#1A1A1A',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                padding: '24px'
              }}
            >
              <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '17px', fontWeight: 500, color: '#FFFFFF', margin: '0 0 10px' }}>
                Immutable Tamper-Proof Audit Logging
              </h3>
              <p style={{ fontSize: '14px', color: '#D7D7D7', lineHeight: 1.6, margin: 0 }}>
                Every API call executed under an impersonation session is logged with dual identity context (<code>adminId</code> + <code>impersonatedUserId</code>), satisfying strict SOC2 Type II, ISO 27001, and HIPAA access audit mandates.
              </p>
            </div>
          </div>
        </section>

        {/* Compliance & Standards */}
        <section id="compliance">
          <div style={{ marginBottom: '16px' }}>
            <div className="eyebrow" style={{ color: '#6D7CFF', marginBottom: '6px' }}>Compliance &amp; Standards</div>
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '24px', fontWeight: 400, color: '#FFFFFF', margin: 0 }}>
              Enterprise Compliance Readiness
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            <div 
              style={{
                backgroundColor: '#1A1A1A',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                padding: '24px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#10B981', marginBottom: '10px' }}>
                <CheckCircle2 size={20} />
                <span style={{ fontSize: '12px', fontWeight: 500, color: '#FFFFFF', fontFamily: "'Outfit', sans-serif" }}>AUDIT READY</span>
              </div>
              <h4 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '17px', fontWeight: 500, color: '#FFFFFF', margin: '0 0 8px' }}>
                SOC2 Type II Architecture
              </h4>
              <p style={{ fontSize: '14px', color: '#D7D7D7', lineHeight: 1.6, margin: 0 }}>
                Full audit logging, tamper-evident trails, and strict role-based access controls across all tenant data pipelines.
              </p>
            </div>

            <div 
              style={{
                backgroundColor: '#1A1A1A',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                padding: '24px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#10B981', marginBottom: '10px' }}>
                <CheckCircle2 size={20} />
                <span style={{ fontSize: '12px', fontWeight: 500, color: '#FFFFFF', fontFamily: "'Outfit', sans-serif" }}>SOVEREIGN DATA</span>
              </div>
              <h4 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '17px', fontWeight: 500, color: '#FFFFFF', margin: '0 0 8px' }}>
                GDPR &amp; DPDP Sovereignty
              </h4>
              <p style={{ fontSize: '14px', color: '#D7D7D7', lineHeight: 1.6, margin: 0 }}>
                Right-to-be-forgotten deletion endpoints, automated data residency routing, and isolated database schemas.
              </p>
            </div>

            <div 
              style={{
                backgroundColor: '#1A1A1A',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                padding: '24px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#10B981', marginBottom: '10px' }}>
                <CheckCircle2 size={20} />
                <span style={{ fontSize: '12px', fontWeight: 500, color: '#FFFFFF', fontFamily: "'Outfit', sans-serif" }}>FIDO ALLIANCE</span>
              </div>
              <h4 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '17px', fontWeight: 500, color: '#FFFFFF', margin: '0 0 8px' }}>
                FIDO2 / WebAuthn v13
              </h4>
              <p style={{ fontSize: '14px', color: '#D7D7D7', lineHeight: 1.6, margin: 0 }}>
                Hardware-backed biometric passkey assertions with zero credential transmission risk or phishing vulnerabilities.
              </p>
            </div>
          </div>
        </section>

      </main>

    </div>
  );
};
